/**
 * Two-stage validation.
 *
 * @Codex's rule from decision 0004, and it is the right one: JSON-valid is not
 * render-valid. Ajv proves shape; the semantic pass proves meaning. Unknown
 * constructs FAIL CLOSED — they are never silently ignored.
 */

import Ajv, { type ValidateFunction } from 'ajv';
import {
  LayoutSpec, type TLayoutSpec, type TNode, type TPlacement, type TInvocation, type TQuery,
} from './schema.js';
import * as V from './vocabulary.js';
import { admits, slotFor, countValues, MAX_VALUES, type NodeKind } from './grammar.js';
import { lookupModule } from './module-registry.js';

/**
 * Role kinds a `media` node may bind.
 *
 * A set rather than a chain of `!==`, because the chain is what let `av` and
 * then `doc` each ship a change that validated everywhere else and died here.
 * The renderer resolves a media node's value out of the blob store instead of
 * printing it, and these are the kinds whose values are blobs.
 */
const MEDIA_NODE_KINDS: ReadonlySet<string> = new Set(['media', 'av', 'doc']);

/**
 * A table read at an AUTHOR-SUPPLIED key, resolving own properties only.
 *
 * Every lookup table here is a plain object literal, so `Object.prototype` is
 * on the end of it: `shapes['toString']`, `channelRoles['constructor']`. Both
 * of those names pass the schema's key patterns, and both come back TRUTHY
 * from a table that declares neither.
 *
 * The three ways that failed, all found from one report by @Codex:
 *
 *   - `channelRoles['constructor']` is the `Object` function, which has no
 *     `.includes` — so `validateSpec` THREW instead of returning problems, and
 *     every caller of it died on an authored document.
 *   - `POOLABLE_CONFIG_SHAPES['constructor']` is the `Object` function, whose
 *     `prototype` and `name` then read as declared config shapes for a module
 *     that has none.
 *   - `shapes['toString']` does the same on a SEEDED capability, with no
 *     registry involved at all — the wider case, which the report did not
 *     name and which was live before module ids became a syntax.
 *
 * In each, the failure is silent-then-open: the key is truthy so the
 * unknown-key arm never fires, and it carries no recognised `type` so every
 * arm of the shape check is false as well. `pool.ts` refuses the same keys
 * because its config check asks `ok` rather than `bad` — which is exactly the
 * property this repo keeps catching in itself, a check that is green about the
 * wrong question, held up by a different layer that happens to be strict.
 */
const own = <T>(table: Readonly<Record<string, T>>, key: string): T | undefined =>
  Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;

export interface Problem {
  path: string;
  code: string;
  message: string;
}

const ajv = new Ajv({ strict: true, allErrors: true, allowUnionTypes: true });
let compiled: ValidateFunction | undefined;

const MAX_DEPTH = 12;
/** A 4096-node invalid tree produced 347k problems and 46 MB of JSON, because
 *  a seven-branch union multiplies errors at every bad node. Cap it. */
const MAX_PROBLEMS = 200;

/**
 * Iterative pre-scan, before Ajv ever sees the value.
 *
 * Ajv's compiled validator recurses over the *data*, so a deeply nested
 * document blows the stack with a RangeError long before the semantic pass's
 * depth guard can run. A caller expecting `{valid:false, problems}` got an
 * uncaught exception instead — from a JSON document, which is exactly what an
 * LLM-generated spec is.
 *
 * The visited set also covers a cyclic in-memory object, since `validateSpec`
 * accepts `unknown` and not only parsed JSON.
 */
function preScan(value: unknown): Problem[] {
  const stack: Array<{ v: unknown; d: number }> = [{ v: value, d: 0 }];
  const seen = new WeakSet<object>();

  // The budget is counted in VALUES and lives in one place. This used to count
  // objects reached by this walk while `pool.ts` counted spec nodes in a
  // different walk — and two counters kept honest by a test is the bug, not the
  // fix. A spec node is not a unit of work either: a card with sixteen bindings
  // is one node and thirty-four values.
  const total = countValues(value, MAX_VALUES);
  if (total > MAX_VALUES) {
    return [{ path: '/', code: 'too-large', message: `more than ${MAX_VALUES} values` }];
  }

  while (stack.length) {
    const { v, d } = stack.pop()!;
    if (v === null || typeof v !== 'object') continue;
    if (seen.has(v)) return [{ path: '/', code: 'cyclic', message: 'value contains a cycle' }];
    seen.add(v);
    if (d > MAX_DEPTH * 4) {
      return [{ path: '/', code: 'max-depth', message: `nesting exceeds ${MAX_DEPTH}` }];
    }
    for (const child of Object.values(v as Record<string, unknown>)) {
      stack.push({ v: child, d: d + 1 });
    }
  }
  return [];
}

function shapeCheck(value: unknown): Problem[] {
  compiled ??= ajv.compile(LayoutSpec);
  if (compiled(value)) return [];
  return (compiled.errors ?? []).slice(0, MAX_PROBLEMS).map((e) => ({
    path: e.instancePath || '/',
    code: 'shape',
    message: `${e.message ?? 'invalid'}${e.params ? ` ${JSON.stringify(e.params)}` : ''}`,
  }));
}

/** Everything JSON Schema structurally cannot say. */
function semanticCheck(spec: TLayoutSpec): Problem[] {
  const problems: Problem[] = [];
  const seenIds = new Map<string, string>();
  const viewStateCount = (spec.viewState ?? []).length;
  /** Anchors reference nodes that may appear LATER in pre-order, so they cannot
   *  be resolved during the walk. Collected here and checked once it finishes. */
  const placed: { path: string; id: string | undefined; placement: TPlacement }[] = [];

  const push = (path: string, code: string, message: string) =>
    problems.push({ path, code, message });

  // Every string in the spec, not just the fields we thought of. Four leaks in
  // this package have now taken the same shape — a field nobody enumerated
  // carrying content — so this walks the whole structure instead of a list.
  (function noExternals(value: unknown, path: string): void {
    if (typeof value === 'string') {
      if (V.looksExternal(value)) {
        push(path, 'external-resource',
          'a layout spec may not reference an external resource; media resolves '
          + 'to a URL at render time through the private binding manifest');
      }
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((v, i) => noExternals(v, `${path}/${i}`));
      return;
    }
    if (value && typeof value === 'object') {
      for (const [k, v] of Object.entries(value)) noExternals(v, `${path}/${k}`);
    }
  })(spec, '');

  function checkQuery(q: TQuery, path: string) {
    // Per-role-kind operator sets. Notion's file property accepting only
    // emptiness tests is not arbitrary — it falls out of what the type answers.
    const walkFilter = (node: unknown, p: string): void => {
      if (!node || typeof node !== 'object') return;
      const n = node as Record<string, unknown>;
      if (Array.isArray(n['all'])) return n['all'].forEach((c, i) => walkFilter(c, `${p}/all/${i}`));
      if (Array.isArray(n['any'])) return n['any'].forEach((c, i) => walkFilter(c, `${p}/any/${i}`));
      if (n['not']) return walkFilter(n['not'], `${p}/not`);
      const w = n['where'] as { role?: V.Role; op?: V.Operator; valueFrom?: number } | undefined;
      if (!w?.role || !w.op) return;
      const kind = V.ROLE_KIND[w.role];
      if (!V.OPERATORS_BY_KIND[kind].includes(w.op)) {
        push(`${p}/where`, 'operator-not-legal-for-kind',
          `operator "${w.op}" is not legal against a ${kind} role ("${w.role}")`);
      }
      const needsValue = w.op !== 'is-empty' && w.op !== 'is-not-empty';
      if (needsValue && w.valueFrom === undefined) {
        push(`${p}/where`, 'predicate-missing-value',
          `operator "${w.op}" needs a value, and a spec may only reference a view-state slot`);
      }
      if (w.valueFrom !== undefined && w.valueFrom >= viewStateCount) {
        push(`${p}/where`, 'undeclared-view-state',
          `predicate reads view-state slot ${w.valueFrom}, which is not declared`);
      }
    };
    if (q.filter) walkFilter(q.filter, `${path}/filter`);

    if (q.subGroupBy && !q.groupBy) {
      push(path, 'subgroup-without-group', 'subGroupBy requires groupBy');
    }
    // `av` as well as `media`. Grouping by a recording makes its opaque value
    // the column heading, which puts a blob locator on screen as a label —
    // and time-based media answers exactly the same operators as a still, so it
    // belongs behind exactly the same guard. Found by @Codex.
    // BOTH groupings, not just the outer one.
    //
    // @Codex: *"the existing `group-by-media` guard checks only `groupBy` …
    // this renderer now formats the expanded object with `String`, so distinct
    // values collapse under headings such as `[object Object]` rather than
    // forming meaningful subgroups; the same kinds are already rejected for the
    // outer grouping for exactly this reason."*
    //
    // The rule was already written one field away — for the sixth time in this
    // repo, and this time I wrote the field it was one away FROM. `subGroupBy`
    // reached the renderer without inheriting the constraint that exists
    // because a heading has to be a name a person can read.
    //
    // A REFERENCE is admitted in the SECOND grouping and refused in the first,
    // and the asymmetry is the two positions' different readers. The outer
    // heading's count comes from the store's fold, which keys by the value it
    // holds — a reference's identity — so a ref-grouped board had store-backed
    // groups that never matched a painted heading. A sub-group never asks the
    // store ("NO COUNT on a sub-heading, ever"), and the renderer names a lane
    // by the referenced object's TITLE: Fibery's swimlanes are a feature's
    // sprint across the state columns, which is exactly this shape.
    const outer = q.groupBy;
    if (outer && V.UNGROUPABLE_KINDS.includes(V.ROLE_KIND[outer])) {
      push(path, 'group-by-media',
        'cannot group by a media, document or reference role');
    }
    const inner = q.subGroupBy;
    if (inner && V.ROLE_KIND[inner] !== 'ref' && V.UNGROUPABLE_KINDS.includes(V.ROLE_KIND[inner])) {
      push(path, 'group-by-media',
        'cannot sub-group by a media or document role');
    }
    for (const a of q.aggregate ?? []) {
      const kind = V.ROLE_KIND[a.role];
      // The store's current fold path extracts numeric/date values for every
      // function except count, including min/max/first/last.
      const numeric = a.fn !== 'count';
      if (numeric && kind !== 'number' && kind !== 'score' && kind !== 'date') {
        push(path, 'aggregation-kind-mismatch',
          `"${a.fn}" cannot fold a ${kind} role ("${a.role}")`);
      }
    }
  }

  /** The grammar, applied at one slot. Two table lookups; the walk is what
   *  costs, and it was happening anyway. */
  function checkAdmits(host: TNode, child: TNode, at: string) {
    const slot = slotFor(host);
    if (!slot) return;
    if (!admits(host.kind as NodeKind, slot, child.kind as NodeKind)) {
      push(at, 'kind-not-admitted',
        `a ${host.kind} does not admit a ${child.kind} in its ${slot}`);
    }
  }

  function walk(
    node: TNode, path: string, depth: number,
    insideFragmenting: boolean, inItemTemplate: boolean,
  ) {
    if (depth > MAX_DEPTH) {
      push(path, 'max-depth', `nesting exceeds ${MAX_DEPTH}`);
      return;
    }
    const id = (node as { id?: string }).id;
    if (id) {
      const prior = seenIds.get(id);
      if (prior) push(path, 'duplicate-id', `id "${id}" already used at ${prior}`);
      else seenIds.set(id, path);
    }

    // An invocation and the affordance that reaches it are two halves of one
    // fact. Either alone is a bug the schema cannot see: a node offering
    // `invoke` with nothing behind it renders a button that does nothing, and
    // an invocation with no `invoke` is behaviour with no way to reach it —
    // which reads, to anyone auditing the spec, as behaviour someone tried to
    // hide.
    // A collection inside an item template renders the whole object set inside
    // every card: sixty rows each containing sixty rows is 3600 card renders
    // from one legal drop. The schema cannot see it — `items[].spec` is
    // `Array<Self>` — and the cost is identical at any depth, so the rule is on
    // the SUBTREE rather than the slot.
    if (node.kind === 'collection' && inItemTemplate) {
      push(path, 'collection-in-item-template',
        'a collection inside a collection item template renders the whole object '
        + 'set once per object');
    }

    const invocation = (node as { invocation?: TInvocation }).invocation;
    const offers = ((node as { affordances?: readonly string[] }).affordances ?? [])
      .includes('invoke');
    if (invocation && !offers) {
      push(`${path}/invocation`, 'invocation-without-affordance',
        'a node carrying an invocation must offer the "invoke" affordance');
    }
    if (offers && !invocation) {
      push(path, 'invoke-without-invocation',
        'a node offering "invoke" must name the action it invokes');
    }
    for (const [i, a] of (invocation?.args ?? []).entries()) {
      const hasRole = a.role !== undefined;
      const valueFrom = a.valueFrom;
      const hasValueFrom = valueFrom !== undefined;
      if (hasRole === hasValueFrom) {
        // Exactly one source. A silent precedence rule would make two authored
        // invocations execute identically while claiming different contracts.
        push(`${path}/invocation/args/${i}`,
          hasRole ? 'argument-multiple-sources' : 'argument-without-source',
          hasRole
            ? 'an argument cannot come from both a role and a view-state slot'
            : 'an argument must come from a role or a view-state slot');
        continue;
      }
      if (hasValueFrom && valueFrom >= viewStateCount) {
        push(`${path}/invocation/args/${i}/valueFrom`, 'view-state-index',
          `no view-state slot ${valueFrom}; the spec declares ${viewStateCount}`);
      }
    }

    const placement = (node as { placement?: TPlacement }).placement;
    if (placement && placement.mode !== 'inline') {
      placed.push({ path, id, placement });
      // A collection's item spec is a TEMPLATE, instantiated once per object. A
      // docked panel there is not one sidebar, it is one sidebar per row — five
      // hundred of them for a five-hundred-row collection, all claiming the same
      // edge. The spec can say it; it can never mean anything.
      if (inItemTemplate) {
        push(`${path}/placement`, 'placement-in-item-template',
          `a collection item spec is instantiated once per object, so "${placement.mode}" `
          + 'placement there would produce one panel per row; place the collection itself');
      }
    }

    const checkBinding = (
      b: {
        direction: string; mutation?: string; role: V.Role; derive?: V.Derivation;
        lookup?: V.Role;
      },
      p: string,
      site: V.BindingSite,
    ) => {
      // Decision 0007: a write binding must declare what it writes, so the
      // runtime can refuse everything it did not declare.
      if (b.direction === 'read-write' && !b.mutation) {
        push(p, 'write-without-mutation',
          'a read-write binding must declare its mutation');
      }
      if (b.direction === 'read' && b.mutation) {
        push(p, 'mutation-without-write',
          'a read-only binding must not declare a mutation');
      }
      // ONE table, not a special case per mutation.
      //
      // This used to check `move-between-groups` alone, and accepted every
      // other pair — so a spec could declare `set-enum` on a text role, or
      // `set-value` on a relation, and validate cleanly before authorization
      // refused it. Measured: of 168 pairs, 144 are illegal and the old check caught 26,
      // so 118 validated cleanly. A
      // spec that validates and can never mean anything is exactly what
      // decision 0007 exists to prevent, and 0016 asks for the table to live
      // in the vocabulary and be read from both sides.
      if (b.mutation !== undefined && V.MUTATIONS.includes(b.mutation as V.Mutation)) {
        const m = b.mutation as V.Mutation;
        // Site first, because it is the coarser refusal and the more useful
        // one: `link` on a field is wrong wherever it points, and telling an
        // author about the role would send them to fix the wrong half.
        if (!V.mutationAcceptsSite(m, site)) {
          push(p, 'mutation-binding-site',
            `"${m}" cannot be declared on a ${site} binding — `
            + `${V.MUTATION_BINDING_SITES[m].join(' or ')} only`);
        } else if (!V.mutationAcceptsRole(m, b.role)) {
          // Two different refusals wearing one code would be a lie: `link` on
          // `source` is a ref, so "it writes ref" reads as agreement.
          if (V.isRelationMutation(m) && V.ROLE_KIND[b.role] === 'ref') {
            push(p, 'mutation-role-not-relation',
              `"${m}" needs a relation role; "${b.role}" is a ref but not an edge `
              + `(${V.RELATION_ROLES.join(', ')})`);
          } else {
            push(p, 'mutation-role-kind',
              `"${m}" cannot be written to role "${b.role}" `
              + `(${V.ROLE_KIND[b.role]}) — it writes `
              + `${V.MUTATION_ROLE_KINDS[m].join(' or ')}`);
          }
        }
      }
      if (b.derive !== undefined) {
        // A derived value has no inverse: nobody can type "1960" into a Year
        // column and have the store work out which day was meant. Refused here
        // rather than dropped at the renderer, which is the shape this repo
        // keeps finding — accepting a value and discarding it.
        if (b.direction === 'read-write') {
          push(p, 'derive-is-read-only',
            `a derived column cannot be written: "${b.derive}" has no inverse`);
        }
        if (!V.derivationAcceptsRole(b.derive, b.role)) {
          push(p, 'derive-role-kind',
            `"${b.derive}" cannot be computed from role "${b.role}" `
            + `(${V.ROLE_KIND[b.role]})`);
        }
      }
      if (b.lookup !== undefined) {
        // A lookup crosses a relation, so the role it is DECLARED ON has to be
        // one. The same check `store.traverse` makes at its own door, stated
        // here too: a spec that validates and then throws at read time is a
        // spec the author cannot fix from the error they were given.
        if (V.ROLE_KIND[b.role] !== 'ref') {
          push(p, 'lookup-not-on-a-reference',
            `a lookup reads across a reference; this binding is on "${b.role}", `
            + `which is ${V.ROLE_KIND[b.role]}`);
        }
        // A lookup OF a lookup is a second hop, and the honest way to say that
        // is a second column. Allowing it would make one binding's meaning
        // depend on data it has never seen — the far object either has
        // references or it does not, and the column would silently be a
        // different thing per row.
        if (V.ROLE_KIND[b.lookup] === 'ref') {
          push(p, 'lookup-reads-a-reference',
            `a lookup reads one value across one hop; "${b.lookup}" is a reference `
            + '— declare a second column rather than a deeper lookup');
        }
        // No inverse, and for a sharper reason than `derive`'s: a write here
        // lands on a DIFFERENT OBJECT than the row it appears in.
        if (b.direction === 'read-write') {
          push(p, 'lookup-is-read-only',
            `a lookup cannot be written: "${b.lookup}" belongs to the object `
            + `"${b.role}" points at, not to this one`);
        }
        // Two producers on one binding. Which wins is a coin toss, and a
        // grammar that accepts the question has already lost the argument.
        if (b.derive !== undefined) {
          push(p, 'lookup-and-derive',
            'a binding computes a value or reads one across a relation, not both');
        }
      }
    };

    switch (node.kind) {
      case 'region':
        node.children.forEach((c, i) => {
          checkAdmits(node, c, `${path}/children/${i}`);
          walk(c, `${path}/children/${i}`, depth + 1, insideFragmenting, inItemTemplate);
        });
        break;
      case 'collection': {
        if (node.query) checkQuery(node.query, `${path}/query`);
        // The message said "every item but the last"; the code flagged ANY item
        // without `when`, which made the canonical shape — N guarded branches
        // plus a trailing default — impossible, and allowed the shape with no
        // default at all. Now it matches what it says.
        // The LAST item must be unguarded, or no branch need match and the
        // collection draws nothing for an object none of them claims. This was
        // survivable while nothing evaluated a guard — the renderer took
        // `itemTemplates[0]` regardless — and became reachable the moment it
        // started choosing per object.
        if (node.items.length > 1 && node.items[node.items.length - 1]!.when) {
          push(`${path}/items/${node.items.length - 1}`, 'polymorphic-default',
            'the last item spec is the default and must not be guarded, '
            + 'or an object matching no guard has nothing to draw it');
        }
        if (node.items.length > 1 && node.items.slice(0, -1).some((it) => !it.when)) {
          push(path, 'polymorphic-without-predicate',
            'a polymorphic collection needs a "when" on every item spec but the last');
        }
        node.items.forEach((it, i) => {
          checkAdmits(node, it.spec, `${path}/items/${i}/spec`);
          walk(it.spec, `${path}/items/${i}/spec`, depth + 1, insideFragmenting, true);
        });
        break;
      }
      case 'card':
        node.bindings.forEach((b, i) => checkBinding(b, `${path}/bindings/${i}`, 'card'));
        (node.children ?? []).forEach((c, i) => {
          checkAdmits(node, c, `${path}/children/${i}`);
          walk(c, `${path}/children/${i}`, depth + 1, insideFragmenting, inItemTemplate);
        });
        break;
      case 'field':
        checkBinding(node.binding, `${path}/binding`, 'field');
        break;
      case 'media':
        checkBinding(node.binding, `${path}/binding`, 'media');
        // `av` counts: a media node is where time-based media renders too, and
        // the ROLE is what tells the renderer which player to reach for. `doc`
        // counts for the same reason — a media node is the only node kind whose
        // value the renderer RESOLVES rather than prints, and a document is a
        // blob like the other two.
        //
        // Named once, so the next kind added does not have to find this line by
        // watching the page say "Generation refused" — which is how this one was
        // found, and it was the fourth such refusal in a single change. Every
        // layer that holds a closed set refused loudly rather than rendering
        // something plausible, which is the design working; the cost is that
        // "add a role kind" is a sweep, and this list is one of its stops.
        if (!MEDIA_NODE_KINDS.has(V.ROLE_KIND[node.binding.role])) {
          push(`${path}/binding`, 'media-binding-kind',
            `a media node needs a ${[...MEDIA_NODE_KINDS].join(', ')} role, `
            + `got "${node.binding.role}"`);
        }
        break;
      case 'node-slot': {
        if (V.ROLE_KIND[node.via] !== 'ref') {
          push(path, 'node-slot-kind', 'node-slot must traverse a ref role');
        }
        // The other half of decision 0016's legality: a node slot is the ONLY
        // site where `link` and `unlink` may be declared, and declaring them
        // there is still not enough — the role it traverses has to be an edge.
        // `source` is a ref and is provenance, so a slot over it may be drawn
        // and may never be attached to.
        const declared = node.mutations ?? [];
        declared.forEach((m, i) => {
          const at = `${path}/mutations/${i}`;
          if (!V.mutationAcceptsSite(m, 'node-slot')) {
            push(at, 'mutation-binding-site',
              `"${m}" cannot be declared on a node slot`);
          } else if (!V.mutationAcceptsRole(m, node.via)) {
            push(at, 'mutation-role-not-relation',
              `"${m}" needs a relation role; "${node.via}" is a ref but not an edge `
              + `(${V.RELATION_ROLES.join(', ')})`);
          }
        });
        // A repeated entry is not harmless: it would mint the same grant twice
        // and runtime selects a grant by gesture, so "which one" becomes a
        // question with two answers.
        if (new Set(declared).size !== declared.length) {
          push(`${path}/mutations`, 'duplicate-mutation',
            'a node slot may declare each relation mutation at most once');
        }
        // The spec a referenced object is drawn with — walked like any other
        // children slot, so `kind-not-admitted`, `duplicate-id`, `max-depth`
        // and every binding rule apply inside it. A hosted spec that nothing
        // validated would be the one place in this product where a tree
        // reaches the renderer unchecked.
        (node.children ?? []).forEach((c, i) => {
          checkAdmits(node, c, `${path}/children/${i}`);
          walk(c, `${path}/children/${i}`, depth + 1, insideFragmenting, inItemTemplate);
        });
        break;
      }
      case 'capability': {
        // THE REGISTRY, not the vocabulary table. A name the schema now accepts
        // syntactically becomes meaningful only by having been registered, and
        // the refusal says so specifically rather than arriving as a shape
        // error with no diagnosis.
        const def = lookupModule(node.capability);
        if (!def) {
          push(`${path}/capability`, 'unregistered-capability',
            `capability "${node.capability}" is not registered`);
          break;
        }
        if (def.version !== node.version) {
          push(path, 'capability-version',
            `registry has ${node.capability}@${def.version}, spec asks for ${node.version}`);
        }
        // The Word/Coda finding: nesting into a fragmenting host is strictly
        // harder than nesting into a grid, and it is the one place a module
        // must expose internal structure to its parent.
        if (insideFragmenting && def.fragmentation === 'atomic') {
          push(path, 'atomic-in-fragmenting-host',
            `${node.capability} is atomic and cannot be split, so a fragmenting host ` +
            `must be told how to keep or overflow it`);
        }
        // Fragmentation is a property of the IMMEDIATE host, not an inherited
        // flag. Once inside an atomic module, the interior is by definition not
        // a fragmenting context — the atomic module cannot be split, so nothing
        // inside it can be either.
        const nowFragmenting = def.fragmentation === 'fragmentable';

        const allowedSlots = def.slots;
        const allowedChannels = def.channels;
        for (const [channel, binding] of Object.entries(node.channels ?? {})) {
          if (!allowedChannels.includes(channel)) {
            push(`${path}/channels/${channel}`, 'unknown-channel',
              `"${channel}" is not a channel ${node.capability} declares`);
          }
          checkBinding(binding, `${path}/channels/${channel}`, 'capability-channel');
          const kind = V.ROLE_KIND[binding.role];
          const admittedRoles = own(def.channelRoles, channel);
          const admittedKinds = own(def.channelRoleKinds, channel);
          if ((admittedRoles !== undefined && !admittedRoles.includes(binding.role))
            || (admittedKinds !== undefined && !admittedKinds.includes(kind))) {
            push(`${path}/channels/${channel}`, 'channel-role-kind',
              `channel "${channel}" on ${node.capability} does not admit role `
              + `"${binding.role}" (${kind})`);
          }
        }
        // Config is owned by the registered descriptor. The same trusted
        // contract validates authored values here and rebuilds them at the pool
        // gate, so a runtime registration cannot validate against one table and
        // disappear against another.
        const shapes = def.configShapes;
        for (const [k, v] of Object.entries(node.config ?? {})) {
          const shape = own(shapes, k);
          if (!shape) {
            push(`${path}/config/${k}`, 'unknown-config-key',
              `"${k}" is not a known config key for ${node.capability}`);
            continue;
          }
          const bad =
            (shape.type === 'boolean' && typeof v !== 'boolean') ||
            (shape.type === 'integer' &&
              (typeof v !== 'number' || !Number.isInteger(v) || v < shape.min || v > shape.max)) ||
            (shape.type === 'enum' && (typeof v !== 'string' || !shape.values.includes(v)));
          if (bad) {
            push(`${path}/config/${k}`, 'config-shape',
              `value does not match the declared shape for "${k}"`);
          }
        }
        for (const slot of Object.keys(node.slots ?? {})) {
          if (!allowedSlots.includes(slot)) {
            push(`${path}/slots/${slot}`, 'unknown-slot',
              `"${slot}" is not a slot ${node.capability} exposes`);
          }
        }
        for (const [slot, kids] of Object.entries(node.slots ?? {})) {
          kids.forEach((c, i) => {
            checkAdmits(node, c, `${path}/slots/${slot}/${i}`);
            walk(c, `${path}/slots/${slot}/${i}`, depth + 1, nowFragmenting, inItemTemplate);
          });
        }
        break;
      }
    }
  }

  walk(spec.root, '/root', 0, false, false);

  // The root IS the surface. Docking it to an edge of itself, or floating it
  // over itself, has no meaning to resolve — there is no outer frame to be
  // placed within.
  const rootPlacement = (spec.root as { placement?: TPlacement }).placement;
  if (rootPlacement && rootPlacement.mode !== 'inline') {
    push('/root/placement', 'placement-on-root',
      'the root node is the surface, so it cannot be placed within itself');
  }

  /**
   * THE PARTITION, checked now that every id is known.
   *
   * @Codex's four refusals from the 0020 ruling, and each one exists because the
   * alternative is a declaration that reads as meaningful and is not:
   *
   *   - **a branch naming no node** — the host would withhold nothing, or draw
   *     a region that is not there, depending on which side read it first;
   *   - **a duplicate branch** — the same region named twice is a partition of
   *     N-1 regions wearing N, and `unset.show` could then match two of them;
   *   - **a fallback outside the branch set** — the unset lens would show a
   *     region the partition does not govern, which is the double-paint defect
   *     with an extra step;
   *   - **a slot index nothing declares** — the control that is supposed to
   *     resolve the partition does not exist, so it never resolves.
   *
   * The bounds check mirrors `predicate reads view-state slot N, which is not
   * declared`, because it is the same mistake about the same array.
   */
  const partition = (spec as { partition?: V.TPartitionShape }).partition;
  if (partition) {
    const slots = spec.viewState?.length ?? 0;
    if (partition.valueFrom >= slots) {
      push('/partition/valueFrom', 'partition-reads-undeclared-slot',
        `partition reads view-state slot ${partition.valueFrom}, and the spec `
        + `declares ${slots}`);
    }
    const seenBranch = new Set<string>();
    partition.branches.forEach((branch, i) => {
      if (!seenIds.has(branch)) {
        push(`/partition/branches/${i}`, 'partition-branch-is-not-a-node',
          `"${branch}" is not the id of any node in this spec`);
      }
      if (seenBranch.has(branch)) {
        push(`/partition/branches/${i}`, 'duplicate-partition-branch',
          `"${branch}" is named twice in one partition`);
      }
      seenBranch.add(branch);
    });
    if (!seenBranch.has(partition.unset.show)) {
      push('/partition/unset/show', 'unset-branch-is-not-a-branch',
        `the unset view shows "${partition.unset.show}", which this partition `
        + 'does not govern');
    }
  }

  // Anchors, now that every id is known.
  const anchorOf = new Map<string, string>();
  for (const p of placed) {
    if (p.placement.mode !== 'anchored') continue;
    const target = p.placement.anchor;
    const targetPath = seenIds.get(target);
    // `placement-in-item-template` refuses a placement INSIDE a template. This
    // is the same argument in the other direction: a template is instantiated
    // once per object, so anchoring to a node inside one anchors to N nodes and
    // there is no answer to which. The dangling check passes because template
    // interiors do have ids.
    if (targetPath !== undefined && /\/items\/\d+\/spec(\/|$)/.test(targetPath)) {
      push(`${p.path}/placement/anchor`, 'anchor-into-item-template',
        `"${target}" is inside a collection item template, so it exists once per `
        + 'object and names no single node to attach to');
      continue;
    }
    if (!seenIds.has(target)) {
      push(`${p.path}/placement/anchor`, 'dangling-anchor',
        `anchored to "${target}", which is not a node in this spec`);
      continue;
    }
    if (p.id !== undefined && target === p.id) {
      push(`${p.path}/placement/anchor`, 'self-anchor',
        'a node cannot be anchored to itself');
      continue;
    }
    if (p.id !== undefined) anchorOf.set(p.id, target);
  }

  // An anchor chain that loops has no fixed point: A sits beside B, which sits
  // beside A, and neither position can be computed first. The schema cannot see
  // this and the renderer would simply not terminate, so it is refused here —
  // the same argument `maxDepth` makes for node-slot in decision 0014.
  for (const start of anchorOf.keys()) {
    const seen = new Set<string>([start]);
    let at = anchorOf.get(start);
    while (at !== undefined) {
      if (seen.has(at)) {
        push(`/root`, 'anchor-cycle',
          `anchor chain from "${start}" loops back to "${at}", so no position resolves first`);
        break;
      }
      seen.add(at);
      at = anchorOf.get(at);
    }
  }

  return problems;
}

export interface ValidationResult {
  valid: boolean;
  problems: Problem[];
}

/**
 * Keys a previous spec version carried and this one refuses BY NAME.
 *
 * Ajv would already reject `editable` — `additionalProperties: false` — with
 * `must NOT have additional properties {"additionalProperty":"editable"}`.
 * That is a true message and a useless one: it says the key is unknown and not
 * what replaced it, and decision 0016 asks specifically that the refusal name
 * `mutations`, because the whole point of retiring the boolean is that nobody
 * should reach for it again.
 *
 * It runs BEFORE the shape check for that reason. Shape errors short-circuit,
 * so a retired key reported there would be the only thing an author saw.
 */
const RETIRED_KEYS: readonly { kind: string; key: string; message: string }[] = [
  {
    kind: 'node-slot', key: 'editable',
    message: 'node-slot.editable is retired: declare `mutations: ("link" | "unlink")[]` '
      + 'instead. It is not an alias — attach and detach are distinct authorities, '
      + 'so a boolean cannot grant one without granting the other',
  },
];

function retiredKeys(value: unknown): Problem[] {
  const problems: Problem[] = [];
  (function walk(v: unknown, path: string): void {
    if (Array.isArray(v)) { v.forEach((c, i) => walk(c, `${path}/${i}`)); return; }
    if (!v || typeof v !== 'object') return;
    const o = v as Record<string, unknown>;
    for (const r of RETIRED_KEYS) {
      if (o['kind'] === r.kind && r.key in o) {
        problems.push({ path: `${path}/${r.key}`, code: 'retired-key', message: r.message });
      }
    }
    for (const [k, c] of Object.entries(o)) walk(c, `${path}/${k}`);
  })(value, '');
  return problems;
}

export function validateSpec(value: unknown): ValidationResult {
  const pre = preScan(value);
  if (pre.length) return { valid: false, problems: pre };
  const retired = retiredKeys(value);
  if (retired.length) return { valid: false, problems: retired };
  const shape = shapeCheck(value);
  if (shape.length) return { valid: false, problems: shape };
  const semantic = semanticCheck(value as TLayoutSpec);
  return { valid: semantic.length === 0, problems: semantic };
}
